import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {EwusVerificationResultDto} from '../types/ewus';
import {RootState} from './store';
import { insuranceDeclarationApi } from '../api/insuranceDeclaration/insuranceDeclarationApi';
import { PatientInsuranceDeclarationDto, RegionalBranch } from '../types/insuranceDeclaration';
import { ewusApi } from '../api/ewus/ewusApi';

const sliceName = 'ewusSlice';
type EwusStatusType = 'idle' | 'loading' | 'failed';

type EwusState = {
    nfzPatientInsuranceDeclaration: boolean | null;
    nfzPatientRegionalBranchCode: string | null;
    nfzPatientRegionalBranch: RegionalBranch | null;
    nfzRegionalBranches: RegionalBranch[];
    result: EwusVerificationResultDto | null;
    status: EwusStatusType | null;
};

const initialState: EwusState = {
    nfzPatientInsuranceDeclaration: null,
    nfzPatientRegionalBranchCode: null,
    nfzPatientRegionalBranch: null,
    nfzRegionalBranches: [],
    result: null,
    status: null,
};



export const fetchPatientNFZRegionalBranchCode = createAsyncThunk<
    PatientInsuranceDeclarationDto,
    {patientId: string},
    unknown
>(
    `${sliceName}/fetchPatientNFZRegionalBranchCode`,
    async ({patientId}) => {
        const { data } = await insuranceDeclarationApi.getInsuranceDeclaration(patientId);

        if (!data.isSuccess) throw new Error('Failed to fetch insurance declaration');
        
        return data?.result;
    },
);

export const fetchEwusVerification = createAsyncThunk<
    EwusVerificationResultDto,
    {appointmentId: number; patientPesel: string},
    unknown
>(
    `${sliceName}/fetchEwusVerification`,
    async ({appointmentId, patientPesel}) => {
        const {data} = await ewusApi.getEwusVerification(
            appointmentId,
            patientPesel,
        );

        if (!data.isSuccess) throw new Error('Failed to fetch EWUS verification');

        return data?.result;
    },
);

export const fetchEwusInitialize = createAsyncThunk<
    EwusVerificationResultDto,
    {appointmentId: number; patientPesel: string},
    unknown
>(
    `${sliceName}/fetchEwusInitialize`,
    async ({appointmentId, patientPesel}) => {
        const {data} = await ewusApi.getInitialize(appointmentId, patientPesel);

        if (!data.isSuccess) throw new Error('Failed to fetch EWUS initialize');

        return data?.result;
    },
);

export const fetchEwusNfzRegionalBranches = createAsyncThunk<
    RegionalBranch[],
    void,
    unknown
>(
    `${sliceName}/fetchEwusNfzRegionalBranches`,
    async () => {
        const {data} = await insuranceDeclarationApi.getNfzRegionalBranch();

        if (!data.isSuccess) throw new Error('Failed to fetch NFZ regional branches');

        return data?.result ?? [];
    },
);

export const setEwusPatientNfzRegionalBranchCode = createAsyncThunk<
    PatientInsuranceDeclarationDto,
    {patientId: string; nfzPatientRegionalBranchCode: string},
    unknown
>(
    `${sliceName}/setEwusPatientNfzRegionalBranchCode`,
    async ({patientId, nfzPatientRegionalBranchCode}) => {
        const {data} = await insuranceDeclarationApi.sendInsuranceDeclaration(
            {
                hasPublicInsurance: true,
                nfzRegionalBranchCode: nfzPatientRegionalBranchCode,
            },
            patientId,
        );

        if (!data.isSuccess) throw new Error('Failed to send insurance declaration');

        return data?.result;
    },
);



const ewusSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        setEwusPatientNfzRegionalBranch: (state, action: PayloadAction<RegionalBranch | null>) => {
            state.nfzPatientRegionalBranch = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchPatientNFZRegionalBranchCode.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchPatientNFZRegionalBranchCode.fulfilled, (state, action) => {
                state.status = 'idle';
                state.nfzPatientInsuranceDeclaration = action.payload?.hasPublicInsurance ?? null;
                state.nfzPatientRegionalBranchCode = action.payload?.nfzRegionalBranchCode ?? null;
                state.nfzPatientRegionalBranch = null;
            })
            .addCase(fetchPatientNFZRegionalBranchCode.rejected, state => {
                state.status = 'failed';
            })
            .addCase(fetchEwusVerification.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchEwusVerification.fulfilled, (state, action) => {
                state.status = 'idle';
                state.result = action.payload;
            })
            .addCase(fetchEwusVerification.rejected, state => {
                state.status = 'failed';
            })
            .addCase(fetchEwusInitialize.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchEwusInitialize.fulfilled, (state, action) => {
                state.status = 'idle';
                state.result = action.payload;
            })
            .addCase(fetchEwusInitialize.rejected, state => {
                state.status = 'failed';
            })
            .addCase(fetchEwusNfzRegionalBranches.fulfilled, (state, action) => {
                state.nfzRegionalBranches = action.payload;
            })
            .addCase(setEwusPatientNfzRegionalBranchCode.pending, state => {
                state.status = 'loading';
            })
            .addCase(setEwusPatientNfzRegionalBranchCode.fulfilled, (state, action) => {
                state.status = 'idle';
                state.nfzPatientRegionalBranchCode = action.payload?.nfzRegionalBranchCode ?? null;
                state.nfzPatientInsuranceDeclaration = action.payload?.hasPublicInsurance ?? null;
                state.nfzPatientRegionalBranch = 
                    state.nfzRegionalBranches.find(b => 
                        b.code === state.nfzPatientRegionalBranchCode) ?? null;
            })
            .addCase(setEwusPatientNfzRegionalBranchCode.rejected, state => {
                state.status = 'failed';
            });
    },
});

export const {setEwusPatientNfzRegionalBranch} = ewusSlice.actions;

export const selectEwusPatientInsuranceDeclaration = (state: RootState & {ewus: EwusState}) =>
    state.ewus.nfzPatientInsuranceDeclaration;

export const selectEwusPatientNfzRegionalBranchCode = (state: RootState & {ewus: EwusState}) =>
    state.ewus.nfzPatientRegionalBranchCode;

export const selectEwusPatientNfzRegionalBranch = (state: RootState & {ewus: EwusState}) =>
    state.ewus.nfzPatientRegionalBranch;

export const selectEwusNfzRegionalBranches = (state: RootState & {ewus: EwusState}) =>
    state.ewus.nfzRegionalBranches;

export const selectEwusResult = (state: RootState & {ewus: EwusState}) =>
    state.ewus.result;

export const selectEwusStatus = (state: RootState & {ewus: EwusState}) =>
    state.ewus.status;

export default ewusSlice.reducer;
